import { useMemoizedFn, useMount } from 'ahooks';
import { Checkbox, Col, message, Modal, Row, Select } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { useAppDispatch } from 'app/hooks';
import { DeleteIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { SectionHeading, TextHeavy } from 'components/RevxTypography';
import {
  EquipmentsAvailablePress,
  EquipmentTypesItemEquipment,
} from 'domains/eqptDomain';
import { StepActionTypeEnum } from 'domains/skuDomain';
import { cloneDeep } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepComponentProps } from '.';
import './Step4Presses.less';
import {
  SpecifySuppliers,
  SpecifySuppliersFlag,
} from '../components/SpecifySuppliers';

const { Option } = Select;

const { confirm } = Modal;

export const Step4Presses = ({ sku, topSku, skuPart }: StepComponentProps) => {
  const [mouseDown, setMouseDown] = useState<boolean>();
  const [selectOpen, setSelectOpen] = useState<boolean>();
  const [selectPresses, setSelectPresses] = useState<Map<string, number[]>>(
    new Map()
  );
  const [allPress, setAllPress] = useState<
    EquipmentsAvailablePress['equipmentTypes']
  >([]);
  const [orgPress, setOrgPress] = useState<EquipmentTypesItemEquipment[]>([]);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const hasTopSku = useMemo(() => {
    return !!topSku;
  }, [topSku]);

  useMount(async () => {
    dispatch.sku.showSkuLoading();
    try {
      dispatch.sku.registerStepAction({
        [StepActionTypeEnum.STEP4_PRESSES]: submit,
      });
      await initPress();
    } finally {
      dispatch.sku.hideSkuLoading();
    }
  });

  const initPress = async () => {
    const {
      currentConfig: { equipmentCodes, exceptEquipmentIds },
      equipmentTypes,
      orgEquipments,
    } = await dispatch.sku.getPresses(Number(sku.id));
    setAllPress(equipmentTypes);
    setOrgPress(orgEquipments);
    const defaultPresses = new Map<string, number[]>();
    equipmentCodes.forEach((code) => {
      defaultPresses.set(
        code,
        (
          equipmentTypes.find((press) => press.equipmentTypeCode === code)
            ?.equipments || []
        )
          .filter((item) => !exceptEquipmentIds?.includes(item.equipmentId))
          .map((item) => item.equipmentId)
      );
    });
    setSelectPresses(defaultPresses);
  };

  const pressQuantity = useMemo(() => {
    return Array.from(selectPresses.values() || []).flat().length;
  }, [selectPresses]);

  const onChange = (value: string) => {
    if (Array.from(selectPresses?.keys() || []).includes(value)) {
      deleteService(value);
    } else {
      const selectServicesClone = cloneDeep(selectPresses);
      selectServicesClone?.set(
        value,
        allPress
          .find((press) => press.equipmentTypeCode === value)
          ?.equipments.filter((item) => item.active)
          .map((item) => item.equipmentId) || []
      );
      setSelectPresses(selectServicesClone);
      // setChangeValue(value);
    }
  };

  const deleteService = (value: string) => {
    const selectServicesClone = cloneDeep(selectPresses);
    selectServicesClone?.delete(value);
    // delete selectServicesClone[value];
    setSelectPresses(selectServicesClone);
  };

  const changeCheckbox = (e: CheckboxValueType[], type: string) => {
    const selectServicesClone = cloneDeep(selectPresses);
    if ((selectServicesClone?.get(type) || []).length > e.length) {
      message.info('That press will no longer be used to calculate a price');
    }
    selectServicesClone?.set(
      type,
      e.map((item) => Number(item))
    );
    setSelectPresses(selectServicesClone);
  };

  const defaultUpdateData = () => {
    const selectPressesClone = cloneDeep(selectPresses);
    selectPressesClone.forEach((value, key) => {
      if (value.length === 0) {
        selectPressesClone.delete(key);
      }
    });
    const allEquipmentId = Array.from(selectPressesClone?.keys() || [])
      .map((item) => {
        return allPress
          .find((press) => press.equipmentTypeCode === item)
          ?.equipments.map((equipment) => equipment.equipmentId);
      })
      .flat()
      .filter((item) => item) as number[];
    const selectEquipment = Array.from(
      selectPressesClone?.values() || []
    ).flat();

    return {
      equipmentCodes: Array.from(selectPressesClone?.keys() || []).map(
        (item) => '' + item
      ),
      exceptEquipmentIds: (allEquipmentId || []).filter(
        (item) => item && !selectEquipment.includes(item)
      ),
      requiredSvcIds: [],
    };
  };

  const submit = useMemoizedFn(async (v) => {
    if (sku.readonly) {
      return true;
    }
    try {
      return await dispatch.sku.updatePresses({
        ...sku,
        ...defaultUpdateData(),
        isSpecifySupplier: v.isSpecifySupplier,
        specifySupplierOrgIds: v.specifySupplierOrgIds || [],
      });
    } catch {
      // do nothing when form invalid
      return false;
    }
  });

  return (
    <>
      <div className="single-sku-config-wizard-step-title">
        <span className="brand-green">
          {t('platform.sku.wizard.managePresses')}
        </span>{' '}
        {t('platform.sku.wizard.toSupportThisSKU')}
      </div>
      {hasTopSku ? (
        <div className="bold mb-20">
          <SpecifySuppliersFlag sku={topSku} isMulti={true} />
        </div>
      ) : (
        <SpecifySuppliers
          sku={sku}
          cb={async (v) => {
            dispatch.sku.showSkuLoading();
            await submit(v);
            await initPress();
            dispatch.sku.hideSkuLoading();
          }}
        />
      )}

      <div className="single-sku-config-wizard-step-content single-sku-config-presses">
        <TextHeavy>{pressQuantity} presses available</TextHeavy>

        <RevxFlexLayout style={{ marginBottom: '12px' }}>
          <Select
            disabled={sku.readonly}
            showSearch
            style={{ width: 260 }}
            placeholder="Select presses to add"
            optionFilterProp="children"
            // onSelect={onChange}
            value={''}
            filterOption={(input, option) => {
              const equipmentTypeName =
                allPress?.find(
                  (item) => item.equipmentTypeCode === option?.value
                )?.equipmentTypeName || '';
              return (
                equipmentTypeName.toLowerCase().indexOf(input?.toLowerCase()) >=
                0
              );
            }}
            getPopupContainer={(e) => {
              return e as HTMLElement;
            }}
            open={selectOpen}
            onDropdownVisibleChange={(open: boolean) => {
              setSelectOpen(open || mouseDown);
            }}
            onMouseEnter={() => {
              setMouseDown(true);
            }}
            onMouseLeave={() => {
              setMouseDown(false);
            }}
          >
            {allPress.map((item) => {
              return (
                <Option
                  className={
                    Array.from(selectPresses?.keys() || []).includes(
                      item.equipmentTypeCode
                    )
                      ? 'ant-select-item-option-selected'
                      : ''
                  }
                  key={item.equipmentTypeCode}
                  value={item.equipmentTypeCode}
                  // disabled={Array.from(selectPresses?.keys() || []).includes(
                  //   item
                  // )}
                >
                  <Checkbox
                    onClick={() => {
                      onChange(item.equipmentTypeCode);
                    }}
                    checked={Array.from(selectPresses?.keys() || []).includes(
                      item.equipmentTypeCode
                    )}
                  >
                    {item.equipmentTypeName}
                  </Checkbox>
                </Option>
              );
            })}
          </Select>

          <RevxButton
            brandGreen
            style={{
              marginLeft: '10px',
            }}
            onClick={() => {
              confirm({
                title: 'Are you want to discard the changes?',
                okText: 'Confirm',
                cancelText: 'Cancel',
                centered: true,
                onOk() {
                  return new Promise(async (resolve) => {
                    resolve('success');
                  });
                },
                onCancel() {},
              });
            }}
          >
            {t('common.revert')}
          </RevxButton>

          <>
            <RevxButton
              brandGreen
              hidden
              style={{
                marginLeft: '10px',
              }}
              onClick={() => {
                const selectServicesClone = cloneDeep(selectPresses);
                allPress.forEach((item) => {
                  selectServicesClone?.set(
                    item.equipmentTypeCode,
                    item?.equipments
                      .filter((item) => item.active)
                      .map((item) => item.equipmentId) || []
                  );
                });
                setSelectPresses(selectServicesClone);
              }}
            >
              {t('common.selectAll')}
            </RevxButton>
            <RevxButton
              brandGreen
              hidden
              style={{
                marginLeft: '10px',
              }}
              onClick={() => {
                setSelectPresses(new Map());
              }}
            >
              {t('common.deselectAll')}
            </RevxButton>
          </>
        </RevxFlexLayout>
        <Row gutter={16}>
          {!sku.isSpecifySupplier && (
            <Col className="gutter-row" span={11} hidden={!orgPress?.length}>
              <RevxFlexLayout
                justify="space-between"
                align="center"
                style={{ marginBottom: '10px' }}
              >
                <SectionHeading>My Orgs presses</SectionHeading>
              </RevxFlexLayout>
              <Checkbox.Group
                disabled={sku.readonly}
                value={
                  Array.from(selectPresses?.keys() || [])
                    .map((item) => selectPresses.get(item) || [])
                    .flat() || []
                }
                onChange={(e) => {
                  const selectServicesClone = cloneDeep(selectPresses);
                  orgPress?.forEach((item) => {
                    if (e.includes(item.equipmentId)) {
                      selectServicesClone?.set(item.equipmentTypeCode, [
                        ...(selectServicesClone.get(item.equipmentTypeCode) ||
                          []),
                        item.equipmentId,
                      ]);
                    } else {
                      selectServicesClone?.set(
                        item.equipmentTypeCode,
                        (
                          selectServicesClone.get(item.equipmentTypeCode) || []
                        ).filter((equipment) => equipment !== item.equipmentId)
                      );
                    }
                  });
                  setSelectPresses(selectServicesClone);
                }}
              >
                {orgPress?.map((cItem) => (
                  <Checkbox
                    value={cItem.equipmentId}
                    key={cItem.equipmentId}
                    disabled={!cItem.active}
                  >
                    <span style={{ fontWeight: 700 }}>
                      {cItem.equipmentManufacturer}
                    </span>
                    {` - ${cItem.equipmentName}`}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Col>
          )}
          {Array.from(selectPresses?.keys() || []).map((item) => {
            const press = allPress.find(
              (press) => press.equipmentTypeCode === item
            );
            const equipmentsItems = allPress.find(
              (press) => press.equipmentTypeCode === item
            )?.equipments;
            return (
              <>
                {!!equipmentsItems?.length && (
                  <Col className="gutter-row" span={11} key={item}>
                    <RevxFlexLayout
                      justify="space-between"
                      align="center"
                      style={{ marginBottom: '10px' }}
                    >
                      <SectionHeading>
                        {press?.equipmentTypeName}
                      </SectionHeading>
                      <div>
                        <RevxButton
                          size="small"
                          brandGreen
                          onClick={() => {
                            if (press?.equipmentTypeCode) {
                              const selectServicesClone =
                                cloneDeep(selectPresses);
                              selectServicesClone?.set(
                                press?.equipmentTypeCode,
                                press?.equipments
                                  .filter((item) => item.active)
                                  .map((item) => item.equipmentId) || []
                              );
                              setSelectPresses(selectServicesClone);
                            }
                          }}
                        >
                          {t('common.selectAll')}
                        </RevxButton>

                        <RevxButton
                          size="small"
                          brandGreen
                          style={{
                            margin: '0 20px 0 10px',
                          }}
                          onClick={() => {
                            if (press?.equipmentTypeCode) {
                              const selectServicesClone =
                                cloneDeep(selectPresses);
                              selectServicesClone?.set(
                                press?.equipmentTypeCode,
                                []
                              );
                              setSelectPresses(selectServicesClone);
                            }
                          }}
                        >
                          {t('common.deselectAll')}
                        </RevxButton>
                        <div
                          hidden={sku.readonly}
                          className="dynamic-delete-button"
                          onClick={() => deleteService(item)}
                        >
                          <DeleteIcon />
                        </div>
                      </div>
                    </RevxFlexLayout>
                    <Checkbox.Group
                      disabled={sku.readonly}
                      value={selectPresses?.get(item)}
                      onChange={(e) => changeCheckbox(e, item)}
                    >
                      {equipmentsItems?.map((cItem) => (
                        <Checkbox
                          value={cItem.equipmentId}
                          key={cItem.equipmentId}
                          disabled={!cItem.active}
                        >
                          <span style={{ fontWeight: 700 }}>
                            {cItem.equipmentManufacturer}
                          </span>
                          {` - ${cItem.equipmentName}`}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Col>
                )}
              </>
            );
          })}
        </Row>
      </div>
    </>
  );
};
